<template>
  <div class="i-payment-table">
    <div class="heading">
      Take a <strong>shortcut</strong> to the most fulfilling & <strong>profitable</strong> years of your career
    </div>
    <div class="pre-sub">Invest in yourself & enjoy lifelong career success</div>
    <div class="points">
      <div class="point">
        <img :src="iconSecure" />
        <div class="text">Secure <span>End-to-end encryption</span></div>
      </div>
      <div class="point">
        <img :src="iconGuarantee" />
        <div class="text">Guarantee <span>30-day money back</span></div>
      </div>
      <div class="point">
        <img :src="iconSupport" />
        <div class="text">24/7 Support <span>Any issues resolved</span></div>
      </div>
    </div>
    <div class="sub">Unlimited access with a flexible subscription — cancel anytime</div>
    <div
      ref="paymentTableRef"
      class="wrapper"
    >
      <template v-if="isXlOrLarger">
        <div class="payment">
          <div
            v-for="card in PAYMENT_DETAILS"
            :key="card.type"
            class="inner"
          >
            <div
              class="card"
              :style="{ '--color': card.color }"
            >
              <div class="type">
                {{ card.type }}
                <div
                  v-if="!!card.key"
                  class="badge"
                >
                  {{ card.key }}
                </div>
              </div>
              <div class="deets">
                <div class="price">{{ card.price }} / month</div>
                <div class="perks">
                  <div class="item"><img :src="card.icon" />Top 10 Career Paths</div>
                  <div class="item"><img :src="card.icon" />Detailed Personality Types & Insights</div>
                  <div class="item"><img :src="card.icon" />Tailored Career Strategy</div>
                  <div class="item"><img :src="card.icon" />Unlimited Quiz Retakes</div>
                  <div class="item"><img :src="card.icon" />Resume & LinkedIn Optimization</div>
                  <div class="item"><img :src="card.icon" />Personalized Job Board</div>
                  <div class="item"><img :src="card.icon" />Job Opening Alerts</div>
                  <div class="item"><img :src="card.icon" />Weekly Live Career Coaching</div>
                  <div class="item"><img :src="card.icon" />2 Hours of Coaching per Month</div>
                </div>
                <div class="footnote">{{ card.footnote }}</div>
              </div>
            </div>
            <iBaseButton
              v-bind="BUTTON_STYLE"
              :background-color="card.color"
              :is-disabled="isPaymentButtonDisabled"
              :icon-url="iconArrowRight"
              icon-width="22px"
              @clicked="firePaymentAction(card.stripePrice)"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="tabs"
          :style="{ '--ac-color': PAYMENT_DETAILS[currentSlide].color }"
        >
          <div
            class="tab"
            :class="{ active: currentSlide == 0 }"
            @click="slideTo(0)"
          >
            Monthly
          </div>
          <div
            class="tab"
            :class="{ active: currentSlide == 1 }"
            @click="slideTo(1)"
          >
            Quarterly
          </div>
          <div
            class="tab"
            :class="{ active: currentSlide == 2 }"
            @click="slideTo(2)"
          >
            Annually
          </div>
        </div>
        <Carousel
          ref="pricingCarousel"
          :autoplay="0"
          :items-to-scroll="1"
          :items-to-show="
            screenWidth / (isDesktopOrLarger ? 500 : isTabletOrLarger ? 450 : isMobileOrLarger ? 470 : 370)
          "
          class="reviews-carousel"
          snap-align="start"
        >
          <Slide
            v-for="card in PAYMENT_DETAILS"
            :key="card.type"
          >
            <div class="inner">
              <div
                class="card"
                :style="{ '--color': card.color }"
              >
                <div class="type">
                  {{ card.type }}
                  <div
                    v-if="!!card.key"
                    class="badge"
                  >
                    {{ card.key }}
                  </div>
                </div>
                <div class="deets">
                  <div class="price">{{ card.price }} / month</div>
                  <div class="perks">
                    <div class="item"><img :src="card.icon" />Top 10 Career Paths</div>
                    <div class="item"><img :src="card.icon" />Detailed Personality Types & Insights</div>
                    <div class="item"><img :src="card.icon" />Tailored Career Strategy</div>
                    <div class="item"><img :src="card.icon" />Unlimited Quiz Retakes</div>
                    <div class="item"><img :src="card.icon" />Resume & LinkedIn Optimization</div>
                    <div class="item"><img :src="card.icon" />Personalized Job Board</div>
                    <div class="item"><img :src="card.icon" />Job Opening Alerts</div>
                    <div class="item"><img :src="card.icon" />Weekly Live Career Coaching</div>
                    <div class="item"><img :src="card.icon" />2 Hours of Coaching per Month</div>
                  </div>
                  <div class="footnote">{{ card.footnote }}</div>
                </div>
              </div>
              <iBaseButton
                v-bind="BUTTON_STYLE"
                :background-color="card.color"
                :is-disabled="isPaymentButtonDisabled"
                :icon-url="iconArrowRight"
                icon-width="22px"
                @clicked="firePaymentAction(card.stripePrice)"
              />
            </div>
          </Slide>
        </Carousel>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue"
import { Carousel, Slide } from "vue3-carousel"
import { useUserStore } from "@/stores/user"
import { white } from "@/styles/colors"
import { breakpointLg, breakpointMd, breakpointSm, breakpointXl } from "@/styles/variables"

import getPaymentAction from "@/composables/getPaymentAction"
import useIsScreenSize from "@/composables/useIsScreenSize"
import useScreenSize from "@/composables/useScreenSize"
import useRandomNumber from "@/composables/useRandomNumber"
import api from "@/services/apiClient"
import GtmService, { UserFlow } from "@/services/gtmService"

// COMPONENTS
import iBaseButton from "@/components/base/BaseButton.vue"

// ASSETS
import iconCheckBlue from "@/assets/icons/icon-check-bluey.svg"
import iconCheckCyan from "@/assets/icons/icon-check-cyan.svg"
import iconCheckPink from "@/assets/icons/icon-check-pink.svg"
import iconSecure from "@/assets/icons/icon-secure.svg"
import iconGuarantee from "@/assets/icons/icon-guarantee.svg"
import iconSupport from "@/assets/icons/icon-support.svg"
import iconArrowRight from "@/assets/icons/icon-arrow-right-white.svg"

// TYPES
import StripePrice from "@/shared/enums/StripePrice"
import { Price } from "@/shared/types/Price"
import { CheckoutSessionItem } from "@/shared/types/CheckoutSession"

// STORES
const userStore = useUserStore()

// COMPOSABLES
const isMobileOrLarger = useIsScreenSize(breakpointSm)
const isTabletOrLarger = useIsScreenSize(breakpointMd)
const isDesktopOrLarger = useIsScreenSize(breakpointLg)
const isXlOrLarger = useIsScreenSize(breakpointXl)
const screenWidth = useScreenSize()
const quarterlyReportPurchases = useRandomNumber(userStore.getFullName || "Full Name", 200, 310)
const annualReportPurchases = useRandomNumber(userStore.getFullName || "Full Name", 90, 170)

// CONSTANTS
const PAYMENT_DETAILS = [
  {
    color: "#47D9C3",
    type: "Monthly",
    price: "$29.99",
    icon: iconCheckCyan,
    stripePrice: StripePrice.REPORT_MONTHLY
  },
  {
    color: "#BB51FF",
    type: "Quarterly",
    price: "$19.99",
    icon: iconCheckPink,
    key: "Most Popular",
    footnote: `${quarterlyReportPurchases.value} people purchased in the last 24 hours`,
    stripePrice: StripePrice.REPORT_QUARTERLY
  },
  {
    color: "#4F99FF",
    type: "Annually",
    price: "$9.99",
    icon: iconCheckBlue,
    key: "Best Value",
    footnote: `${annualReportPurchases.value} people purchased recently`,
    stripePrice: StripePrice.REPORT_ANNUAL
  }
]
const BUTTON_STYLE = {
  text: "Subscribe Today",
  borderRadius: "8px",
  fontWeight: "500",
  textColor: white,
  width: "100%",
  padding: "10px 25px",
  size: "medium"
}

// FUNCTIONS
const disablePaymentButtons = () => {
  isPaymentButtonDisabled.value = true
}

const enablePaymentButtons = () => {
  isPaymentButtonDisabled.value = false
}

const slideTo = (index: number) => {
  if (pricingCarousel.value) {
    pricingCarousel.value.slideTo(index, false)
    currentSlide.value = index
  } else return
}

const firePaymentAction = async (stripePrice: StripePrice) => {
  if (isPaymentButtonDisabled.value) return
  disablePaymentButtons()
  try {
    await getPaymentAction([stripePrice], UserFlow.REPORT, enablePaymentButtons)()
  } catch {
    enablePaymentButtons()
  }
}

/**
 * Fetch the prices and their information from the API
 */
const fetchPrices = async () => {
  try {
    const response = await api.stripe.getPrices([
      StripePrice.REPORT_MONTHLY,
      StripePrice.REPORT_QUARTERLY,
      StripePrice.REPORT_ANNUAL
    ])
    const pricesArr = response.prices
    prices.value = {
      monthly: pricesArr.find((p) => p.uuid === StripePrice.REPORT_MONTHLY) || null,
      quarterly: pricesArr.find((p) => p.uuid === StripePrice.REPORT_QUARTERLY) || null,
      annual: pricesArr.find((p) => p.uuid === StripePrice.REPORT_ANNUAL) || null
    }
  } catch (error) {
    console.error("Failed to load prices", error)
  }
}

const sendGTMEvent = async () => {
  const items: CheckoutSessionItem[] = []

  if (prices.value.monthly) {
    items.push({
      id: prices.value.monthly.productId,
      name: prices.value.monthly.name,
      price: prices.value.monthly.price / 100,
      quantity: 1
    })
  }

  if (prices.value.quarterly) {
    items.push({
      id: prices.value.quarterly.productId,
      name: prices.value.quarterly.name,
      price: prices.value.quarterly.price / 100,
      quantity: 1
    })
  }

  if (prices.value.annual) {
    items.push({
      id: prices.value.annual.productId,
      name: prices.value.annual.name,
      price: prices.value.annual.price / 100,
      quantity: 1
    })
  }

  await GtmService.viewItemListEvent(UserFlow.REPORT, items)
}

// REFS
/**
 * Prices for the different report types. Fetched from the API.
 */
const prices = ref<Record<"monthly" | "quarterly" | "annual", Price | null>>({
  monthly: null,
  quarterly: null,
  annual: null
})

const pricingCarousel = ref<typeof Carousel | null>(null)
const currentSlide = ref<number>(0)

/**
 * Whether the payment buttons should be disabled
 * - We want to disable this when the user is already in the process of starting a checkout session
 */
const isPaymentButtonDisabled = ref(false)

const paymentTableRef = ref<HTMLElement | null>(null)

// LIFECYCLE
onMounted(() => {
  fetchPrices().then(() => {
    enablePaymentButtons()
  })

  // Send GTM event when the payment table is in view
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sendGTMEvent()
          obs.disconnect() // trigger only once
        }
      })
    },
    { threshold: 0.1 }
  )
  if (paymentTableRef.value) observer.observe(paymentTableRef.value)
})
</script>

<style lang="scss" scoped>
.i-payment-table {
  @include flex(column, center, center, $gap: 30px);

  .heading {
    @include display-md;

    text-align: center;
    font-weight: 300px;
    max-width: 640px;
    font-style: italic;
  }

  .pre-sub {
    @include display-xs;

    text-align: center;
    font-weight: 200px;
    font-style: italic;
  }

  .points {
    @include flex(column, center, stretch, $gap: 15px);

    font-weight: 300;
    margin: 20px auto;

    @media screen and (min-width: $breakpoint-lg) {
      @include flex(row, center, stretch, $gap: 40px);
    }

    .point {
      @include flex(row, start, center, $gap: 12px);
      @include border-radius-md;
      @include text-md;

      font-weight: 300;
      background-color: $white;
      box-shadow: 0 4px 16px 0 $black100;
      padding: 14px 18px;

      .text {
        @include flex(column, start, start);
        @include text-lg;

        span {
          @include text-sm;
        }
      }
    }
  }

  .sub {
    @include display-xs;

    text-align: center;
    font-weight: 200px;
  }

  .wrapper {
    @include flex(column, center, center, $gap: 20px);

    width: 100%;

    .payment {
      @include grid($cols: repeat(3, 372px), $rows: 1fr, $align-items: stretch, $gap: 16px);

      margin: 30px auto;
    }

    .inner {
      @include flex(column, space-between, stretch, $gap: 18px);

      .card {
        @include flex(column, start, stretch, $gap: 25px);
        @include border-radius-md;
        @include text-md;

        text-align: start;
        height: 100%;
        font-weight: 300;
        background-color: $white;
        box-shadow: 0 4px 16px 0 $black100;
        padding: 20px;

        .type {
          @include display-xs;
          @include flex(row, space-between, center, $gap: 12px);

          color: var(--color);

          @media screen and (min-width: $breakpoint-sm) {
            @include display-sm;
          }

          .badge {
            @include border-radius-xxl;
            @include text-sm;

            background-color: var(--color);
            padding: 6px 10px;
            color: $white;

            @media screen and (min-width: $breakpoint-sm) {
              padding: 10px 12px;
            }
          }
        }

        .deets {
          @include flex(column, start, stretch, $gap: 12px);

          .price {
            @include text-xl;

            font-style: italic;

            @media screen and (min-width: $breakpoint-sm) {
              @include display-xs;
            }
          }

          .perks {
            @include flex(column, start, start, $gap: 8px);
            @include text-sm;

            @media screen and (min-width: $breakpoint-sm) {
              @include text-md;

              gap: 14px;
            }

            .item {
              @include flex(row, start, center, $gap: 10px);
            }

            img {
              width: 24px;
              height: 24px;
            }
          }

          .footnote {
            @include text-xs;

            text-align: center;
            color: var(--color);
          }
        }
      }
    }

    .tabs {
      @include flex(row, center, center);
      @include border-radius-sm;

      overflow: hidden;

      .tab {
        @include text-sm;

        font-weight: 600;
        color: $black100;
        background-color: black;
        cursor: pointer;
        padding: 15px 16px;

        &:not(:last-child) {
          border-right: 1px solid $black800;
        }

        &.active {
          background-color: var(--ac-color);
          color: $black;
        }
      }
    }

    .reviews-carousel {
      width: 100%;

      .inner {
        height: 100%;
        width: 320px;

        @media screen and (min-width: $breakpoint-sm) {
          width: 372px;
        }
      }
    }
  }
}
</style>
